import axios from 'axios';

const api = axios.create({
  baseURL:         import.meta.env.VITE_API_URL || '/api',
  timeout:         15000,
  withCredentials: true,          // send httpOnly refresh cookie
  headers: { 'Content-Type': 'application/json' },
});

// ─── Request interceptor ──────────────────────────────────────────────────────
api.interceptors.request.use(
  (config) => config,
  (error)  => Promise.reject(error)
);

// ─── Single-flight session refresh ────────────────────────────────────────────
// The refresh cookie is single-use and ROTATES on every /auth/refresh (the
// backend deletes the old token id and issues a new one). So two concurrent
// refresh calls carrying the same cookie race: the first rotates + invalidates
// it, the second gets 401 "Token revoked" and logs the user out. A page load
// has exactly this hazard — AuthContext's session-restore effect and the 401
// interceptor below can both want to refresh. Funnelling every refresh through
// ONE shared in-flight promise collapses them into a single network call, so
// the cookie rotates exactly once. Both this module and AuthContext use it.
let refreshPromise = null;
export function refreshSession() {
  if (!refreshPromise) {
    refreshPromise = api.post('/auth/refresh').finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// ─── Response interceptor — silent token refresh ──────────────────────────────
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    const isRefreshCall = original?.url?.includes('/auth/refresh');
    // Exclude the refresh call itself: its own 401 (no/expired cookie) must NOT
    // trigger another refresh, or an anonymous visitor deadlocks on load.
    if (error.response?.status === 401 && !original._retry && !isRefreshCall) {
      original._retry = true;
      try {
        // Shared single-flight: N concurrent 401s all await one refresh, then
        // each retries its own original request with the new token.
        const { data } = await refreshSession();
        api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`;
        original.headers['Authorization'] = `Bearer ${data.accessToken}`;
        return api(original);
      } catch (refreshError) {
        // Let AuthContext handle the logout.
        window.dispatchEvent(new CustomEvent('gl:session-expired'));
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
